/**
 * Document-tag (doctag) operations — assign, remove, generate.
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData, requireOk } from '../errors.js'

// A doctag note is grounded by a flat list of source passages, not a
// per-statement citation map (CitationData is still used for message citations).
type CitationSources = components['schemas']['CitationSources']

export async function assignDocTags(
  arbi: ArbiClient,
  tagId: string,
  docIds: string[],
  note?: string | null
) {
  return requireData(
    await arbi.fetch.POST('/v1/document/doctag', {
      body: { tag_ext_id: tagId, doc_ext_ids: docIds, note: note ?? null },
    }),
    'Failed to create doctags'
  )
}

export async function removeDocTags(arbi: ArbiClient, tagId: string, docIds: string[]) {
  requireOk(
    await arbi.fetch.DELETE('/v1/document/doctag', {
      body: { tag_ext_id: tagId, doc_ext_ids: docIds },
    }),
    'Failed to delete doctags'
  )
}

export async function updateDocTag(
  arbi: ArbiClient,
  tagId: string,
  docId: string,
  updates: { note?: string | null; citations?: CitationSources | null }
) {
  return requireData(
    await arbi.fetch.PATCH('/v1/document/doctag', {
      body: { tag_ext_id: tagId, doc_ext_id: docId, ...updates },
    }),
    'Failed to update doctag'
  )
}

export async function generateDocTags(
  arbi: ArbiClient,
  tagIds: string[],
  docIds: string[],
  options?: { overwrite?: boolean }
) {
  return requireData(
    await arbi.fetch.POST('/v1/document/doctag/generate', {
      // overwrite regenerates values for documents that already carry the tag;
      // the default skips them (the server's historical behaviour).
      body: { tag_ext_ids: tagIds, doc_ext_ids: docIds, overwrite: options?.overwrite ?? false },
    }),
    'Failed to generate doctags'
  )
}
